import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import { Badge } from "../../../../components/ui/badge";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "../../../../components/ui/collapsible";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../../components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import {
    ArrowLeft,
    ChevronDown,
    Loader2,
    Plus,
    PlugZap,
    Send,
    Shield,
    Trash2,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useToast } from "../../../../hooks/use-toast";
import { myLocalStorage } from "../../../StorageHelper";
import {
    deleteSiemConfiguration,
    fetchSiemConfigurations,
    fetchSiemFilterOptions,
    saveSiemConfiguration,
    sendSiemTestEvent,
    SiemMessageFormat,
    SiemProtocol,
    SiemTlsMode,
    SiemTlsVersion,
    TenantSiemConfiguration,
    TenantSiemConfigurationRequest,
    testSiemConnection,
} from "../Api/siemConfigurationApi";

const DEFAULT_EVENT_CATEGORIES = ["Audit", "Alerts", "AI"] as const;
const DEFAULT_SEVERITIES = ["Low", "Medium", "High", "Critical"] as const;

const emptyForm = (): TenantSiemConfigurationRequest => ({
    destinationName: "",
    enabled: false,
    host: "",
    port: 1514,
    protocol: "TCP",
    messageFormat: "CEF",
    connectionTimeoutSec: 10,
    readTimeoutSec: 30,
    tlsMode: "NONE",
    verifyServerCertificate: true,
    caCertificate: "",
    caCertificateFilename: "",
    clearCaCertificate: false,
    clientKeystore: "",
    clientKeystoreFilename: "",
    clearClientKeystore: false,
    clientKeystorePassword: "",
    clearClientKeystorePassword: false,
    clientCertificate: "",
    clientCertificateFilename: "",
    clearClientCertificate: false,
    clientPrivateKey: "",
    clientPrivateKeyFilename: "",
    clearClientPrivateKey: false,
    privateKeyPassword: "",
    clearPrivateKeyPassword: false,
    tlsVersion: "AUTO",
    keepAlive: true,
    retryAttempts: 3,
    retryIntervalSec: 30,
    queueCapacity: 1000,
    severityFilter: "",
    eventCategoryFilter: "",
});

type TlsStoredFlags = {
    hasCaCertificate: boolean;
    caCertificateFilename: string;
    hasClientKeystore: boolean;
    clientKeystoreFilename: string;
    hasClientKeystorePassword: boolean;
    hasClientCertificate: boolean;
    clientCertificateFilename: string;
    hasClientPrivateKey: boolean;
    clientPrivateKeyFilename: string;
    hasPrivateKeyPassword: boolean;
};

const emptyStoredFlags = (): TlsStoredFlags => ({
    hasCaCertificate: false,
    caCertificateFilename: "",
    hasClientKeystore: false,
    clientKeystoreFilename: "",
    hasClientKeystorePassword: false,
    hasClientCertificate: false,
    clientCertificateFilename: "",
    hasClientPrivateKey: false,
    clientPrivateKeyFilename: "",
    hasPrivateKeyPassword: false,
});

type TlsMaterialFieldProps = {
    label: string;
    accept: string;
    testId: string;
    stored: boolean;
    storedFilename?: string;
    pendingFilename?: string;
    pendingLoaded?: boolean;
    markedForRemoval?: boolean;
    onUpload: (file?: File) => void;
    onRemove: () => void;
};

function TlsMaterialField({
    label,
    accept,
    testId,
    stored,
    storedFilename,
    pendingFilename,
    pendingLoaded,
    markedForRemoval,
    onUpload,
    onRemove,
}: TlsMaterialFieldProps) {
    const displayName = pendingFilename || storedFilename || "";
    const isConfigured = (stored || pendingLoaded) && !markedForRemoval;

    return (
        <div className="space-y-2 rounded-xl border border-border/50 p-3">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                    <Label className="text-sm font-medium">{label}</Label>
                    {isConfigured ? (
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary" className="font-normal">
                                {pendingLoaded ? "Ready to save" : "Configured"}
                            </Badge>
                            {displayName ? (
                                <span className="truncate text-sm text-muted-foreground" title={displayName}>
                                    {displayName}
                                </span>
                            ) : (
                                <span className="text-sm text-muted-foreground">Stored on server</span>
                            )}
                        </div>
                    ) : markedForRemoval ? (
                        <p className="text-sm text-amber-600 dark:text-amber-400">
                            Will be removed on save
                        </p>
                    ) : (
                        <p className="text-sm text-muted-foreground">Not configured</p>
                    )}
                </div>
                {isConfigured && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-destructive hover:text-destructive"
                        onClick={onRemove}
                        data-testid={`${testId}-remove`}
                    >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Remove
                    </Button>
                )}
            </div>
            <Input
                type="file"
                accept={accept}
                onChange={(e) => onUpload(e.target.files?.[0])}
                data-testid={testId}
            />
            <p className="text-xs text-muted-foreground">
                {isConfigured ? "Choose a file to replace, or remove and save." : "Upload a file, then save."}
            </p>
        </div>
    );
}

const parseCsv = (value?: string) =>
    (value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

const toCsv = (values: string[]) => values.join(",");

const toggleCsvValue = (csv: string | undefined, value: string, checked: boolean) => {
    const current = new Set(parseCsv(csv));
    if (checked) {
        current.add(value);
    } else {
        current.delete(value);
    }
    return toCsv(Array.from(current));
};

const readFileContent = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        const isBinary = /\.(p12|pfx)$/i.test(file.name);
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error || new Error("Unable to read file."));
        if (isBinary) {
            reader.readAsDataURL(file);
        } else {
            reader.readAsText(file);
        }
    });

export default function SiemConnectionPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testingConnection, setTestingConnection] = useState(false);
    const [sendingTestEvent, setSendingTestEvent] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [destinations, setDestinations] = useState<TenantSiemConfiguration[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [eventCategories, setEventCategories] = useState<string[]>([...DEFAULT_EVENT_CATEGORIES]);
    const [severities, setSeverities] = useState<string[]>([...DEFAULT_SEVERITIES]);
    const [form, setForm] = useState<TenantSiemConfigurationRequest>(emptyForm());
    const [storedFlags, setStoredFlags] = useState<TlsStoredFlags>(emptyStoredFlags());
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    const isTlsTransport = form.protocol === "TLS";
    const tlsMode = isTlsTransport ? form.tlsMode || "TLS" : "NONE";
    const isServerTls = isTlsTransport && tlsMode === "TLS";
    const isMutualTls = isTlsTransport && tlsMode === "MTLS";
    const enabledCount = useMemo(
        () => destinations.filter((item) => item.enabled).length,
        [destinations],
    );

    const loadDestinations = async (preferId?: number | null) => {
        if (!tenantName) return;
        setLoading(true);
        try {
            const [configs, filters] = await Promise.all([
                fetchSiemConfigurations(tenantName),
                fetchSiemFilterOptions(tenantName).catch(() => null),
            ]);
            setDestinations(configs);
            if (filters?.eventCategories?.length) {
                setEventCategories(filters.eventCategories);
            }
            if (filters?.severities?.length) {
                setSeverities(filters.severities);
            }

            const nextId =
                preferId !== undefined
                    ? preferId
                    : selectedId && configs.some((item) => item.id === selectedId)
                      ? selectedId
                      : configs[0]?.id ?? null;

            if (nextId != null) {
                const selected = configs.find((item) => item.id === nextId);
                if (selected) {
                    applyDestination(selected);
                    return;
                }
            }
            resetForm();
        } catch (error) {
            console.error("Failed to load SIEM configurations:", error);
            toast({
                title: "Load Failed",
                description: "Unable to load SIEM destinations.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDestinations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantName]);

    const applyDestination = (config: TenantSiemConfiguration) => {
        setSelectedId(config.id ?? null);
        setStoredFlags({
            hasCaCertificate: Boolean(config.hasCaCertificate),
            caCertificateFilename: config.caCertificateFilename || "",
            hasClientKeystore: Boolean(config.hasClientKeystore),
            clientKeystoreFilename: config.clientKeystoreFilename || "",
            hasClientKeystorePassword: Boolean(config.hasClientKeystorePassword),
            hasClientCertificate: Boolean(config.hasClientCertificate),
            clientCertificateFilename: config.clientCertificateFilename || "",
            hasClientPrivateKey: Boolean(config.hasClientPrivateKey),
            clientPrivateKeyFilename: config.clientPrivateKeyFilename || "",
            hasPrivateKeyPassword: Boolean(config.hasPrivateKeyPassword),
        });
        setForm({
            id: config.id,
            destinationName: config.destinationName || "",
            enabled: config.enabled ?? false,
            host: config.host || "",
            port: config.port || 1514,
            protocol: (config.protocol || "TCP") as SiemProtocol,
            messageFormat: (config.messageFormat || "CEF") as SiemMessageFormat,
            connectionTimeoutSec: config.connectionTimeoutSec ?? 10,
            readTimeoutSec: config.readTimeoutSec ?? 30,
            tlsMode: (config.tlsMode || (config.protocol === "TLS" ? "TLS" : "NONE")) as SiemTlsMode,
            verifyServerCertificate: config.verifyServerCertificate ?? true,
            caCertificate: "",
            caCertificateFilename: config.caCertificateFilename || "",
            clearCaCertificate: false,
            clientKeystore: "",
            clientKeystoreFilename: config.clientKeystoreFilename || "",
            clearClientKeystore: false,
            clientKeystorePassword: "",
            clearClientKeystorePassword: false,
            clientCertificate: "",
            clientCertificateFilename: config.clientCertificateFilename || "",
            clearClientCertificate: false,
            clientPrivateKey: "",
            clientPrivateKeyFilename: config.clientPrivateKeyFilename || "",
            clearClientPrivateKey: false,
            privateKeyPassword: "",
            clearPrivateKeyPassword: false,
            tlsVersion: (config.tlsVersion || "AUTO") as SiemTlsVersion,
            keepAlive: config.keepAlive ?? true,
            retryAttempts: config.retryAttempts ?? 3,
            retryIntervalSec: config.retryIntervalSec ?? 30,
            queueCapacity: config.queueCapacity ?? 1000,
            severityFilter: config.severityFilter || "",
            eventCategoryFilter: config.eventCategoryFilter || "",
        });
    };

    const resetForm = () => {
        setSelectedId(null);
        setStoredFlags(emptyStoredFlags());
        setForm(emptyForm());
    };

    const updateField = <K extends keyof TenantSiemConfigurationRequest>(
        key: K,
        value: TenantSiemConfigurationRequest[K],
    ) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleUploadField = async (
        field: keyof TenantSiemConfigurationRequest,
        file?: File,
        filenameField?: keyof TenantSiemConfigurationRequest,
        clearField?: keyof TenantSiemConfigurationRequest,
    ) => {
        if (!file) return;
        try {
            const content = await readFileContent(file);
            setForm((prev) => ({
                ...prev,
                [field]: content,
                ...(filenameField ? { [filenameField]: file.name } : {}),
                ...(clearField ? { [clearField]: false } : {}),
            }));
            toast({
                title: "File Loaded",
                description: `${file.name} ready — save to apply.`,
            });
        } catch (error) {
            console.error("Failed to read certificate file:", error);
            toast({
                title: "Upload Failed",
                description: `Unable to read ${file.name}.`,
                variant: "destructive",
            });
        }
    };

    const markTlsRemoval = (
        clearField: keyof TenantSiemConfigurationRequest,
        contentField: keyof TenantSiemConfigurationRequest,
        filenameField?: keyof TenantSiemConfigurationRequest,
        extraClears?: Array<keyof TenantSiemConfigurationRequest>,
    ) => {
        setForm((prev) => {
            const next: TenantSiemConfigurationRequest = {
                ...prev,
                [clearField]: true,
                [contentField]: "",
            };
            if (filenameField) {
                (next as unknown as Record<string, unknown>)[filenameField as string] = "";
            }
            for (const key of extraClears || []) {
                (next as unknown as Record<string, unknown>)[key as string] =
                    typeof prev[key] === "boolean" ? true : "";
            }
            return next;
        });
        setStoredFlags((prev) => {
            const next = { ...prev };
            if (clearField === "clearCaCertificate") {
                next.hasCaCertificate = false;
                next.caCertificateFilename = "";
            }
            if (clearField === "clearClientKeystore") {
                next.hasClientKeystore = false;
                next.clientKeystoreFilename = "";
                next.hasClientKeystorePassword = false;
            }
            if (clearField === "clearClientCertificate") {
                next.hasClientCertificate = false;
                next.clientCertificateFilename = "";
            }
            if (clearField === "clearClientPrivateKey") {
                next.hasClientPrivateKey = false;
                next.clientPrivateKeyFilename = "";
                next.hasPrivateKeyPassword = false;
            }
            if (clearField === "clearClientKeystorePassword") {
                next.hasClientKeystorePassword = false;
            }
            if (clearField === "clearPrivateKeyPassword") {
                next.hasPrivateKeyPassword = false;
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!tenantName) return;

        if (!form.destinationName.trim() || !form.host.trim() || !form.port) {
            toast({
                title: "Missing Information",
                description: "Destination name, host, and port are required.",
                variant: "destructive",
            });
            return;
        }

        if (isMutualTls) {
            const hasKeystore =
                (Boolean(form.clientKeystore?.trim()) || storedFlags.hasClientKeystore) &&
                !form.clearClientKeystore;
            const hasPemClientCertificate =
                (Boolean(form.clientCertificate?.trim()) || storedFlags.hasClientCertificate) &&
                !form.clearClientCertificate;
            const hasPemClientKey =
                (Boolean(form.clientPrivateKey?.trim()) || storedFlags.hasClientPrivateKey) &&
                !form.clearClientPrivateKey;

            if (!hasKeystore && !(hasPemClientCertificate && hasPemClientKey)) {
                toast({
                    title: "Missing mTLS Material",
                    description:
                        "Upload either a .p12/.pfx client identity or both a client certificate and private key.",
                    variant: "destructive",
                });
                return;
            }
        }

        setSaving(true);
        try {
            const saved = await saveSiemConfiguration(tenantName, {
                ...form,
                destinationName: form.destinationName.trim(),
                host: form.host.trim(),
                tlsMode: isTlsTransport ? tlsMode : "NONE",
                caCertificate: form.caCertificate?.trim() || undefined,
                caCertificateFilename: form.caCertificateFilename?.trim() || undefined,
                clearCaCertificate: form.clearCaCertificate || undefined,
                clientKeystore: form.clientKeystore?.trim() || undefined,
                clientKeystoreFilename: form.clientKeystoreFilename?.trim() || undefined,
                clearClientKeystore: form.clearClientKeystore || undefined,
                clientKeystorePassword: form.clientKeystorePassword?.trim() || undefined,
                clearClientKeystorePassword: form.clearClientKeystorePassword || undefined,
                clientCertificate: form.clientCertificate?.trim() || undefined,
                clientCertificateFilename: form.clientCertificateFilename?.trim() || undefined,
                clearClientCertificate: form.clearClientCertificate || undefined,
                clientPrivateKey: form.clientPrivateKey?.trim() || undefined,
                clientPrivateKeyFilename: form.clientPrivateKeyFilename?.trim() || undefined,
                clearClientPrivateKey: form.clearClientPrivateKey || undefined,
                privateKeyPassword: form.privateKeyPassword?.trim() || undefined,
                clearPrivateKeyPassword: form.clearPrivateKeyPassword || undefined,
            });
            toast({
                title: "SIEM Destination Saved",
                description: saved.enabled
                    ? `${saved.destinationName} is enabled for event forwarding.`
                    : `${saved.destinationName} saved. Enable it to start forwarding.`,
            });
            await loadDestinations(saved.id ?? null);
        } catch (error) {
            console.error("Failed to save SIEM configuration:", error);
            toast({
                title: "Save Failed",
                description: "Unable to save SIEM destination.",
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        if (!tenantName || selectedId == null) {
            toast({
                title: "Save First",
                description: "Save the destination before testing the connection.",
                variant: "destructive",
            });
            return;
        }

        setTestingConnection(true);
        try {
            const result = await testSiemConnection(tenantName, selectedId);
            toast({
                title: result.success ? "Connection Successful" : "Connection Failed",
                description: result.message,
                variant: result.success ? "default" : "destructive",
            });
        } catch (error) {
            console.error("SIEM connection test failed:", error);
            toast({
                title: "Connection Failed",
                description: "Unable to reach the SIEM destination. Verify host, port, and transport.",
                variant: "destructive",
            });
        } finally {
            setTestingConnection(false);
        }
    };

    const handleSendTestEvent = async () => {
        if (!tenantName || selectedId == null) {
            toast({
                title: "Save First",
                description: "Save the destination before sending a test event.",
                variant: "destructive",
            });
            return;
        }

        setSendingTestEvent(true);
        try {
            const result = await sendSiemTestEvent(tenantName, selectedId);
            toast({
                title: result.success ? "Test Event Sent" : "Test Event Failed",
                description: result.message,
                variant: result.success ? "default" : "destructive",
            });
        } catch (error) {
            console.error("SIEM test event failed:", error);
            toast({
                title: "Test Event Failed",
                description: "Unable to send a test CEF event to the SIEM destination.",
                variant: "destructive",
            });
        } finally {
            setSendingTestEvent(false);
        }
    };

    const handleDelete = async () => {
        if (!tenantName || selectedId == null) return;

        try {
            await deleteSiemConfiguration(tenantName, selectedId);
            toast({
                title: "Destination Removed",
                description: "The SIEM destination was deleted.",
            });
            await loadDestinations(null);
        } catch (error) {
            console.error("Failed to delete SIEM configuration:", error);
            toast({
                title: "Delete Failed",
                description: "Unable to delete SIEM destination.",
                variant: "destructive",
            });
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    return (
        <div className="tenant-settings-page flex-1 space-y-6 p-6">
            <Card className="settings-header-card border-border/40 rounded-2xl">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => navigate("/tenantSettings")}
                                className="settings-back-button"
                                aria-label="Back to settings"
                                title="Back to settings"
                                data-testid="button-back-siem-settings"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-siem-connection-title">
                                    SIEM Connection
                                </h1>
                                <p className="text-muted-foreground">
                                    Forward normalized security events to your SIEM over Syslog using CEF.
                                </p>
                            </div>
                        </div>
                        <Badge variant={enabledCount > 0 ? "default" : "secondary"}>
                            {enabledCount > 0
                                ? `${enabledCount} Destination${enabledCount === 1 ? "" : "s"} Active`
                                : "No Active Destinations"}
                        </Badge>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading SIEM destinations...
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6">
                    <Card className="border-border/40 rounded-2xl h-fit">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between gap-2">
                                <CardTitle className="text-base">Destinations</CardTitle>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="gap-1"
                                    onClick={resetForm}
                                    data-testid="button-add-siem-destination"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {destinations.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-4">
                                    No destinations yet. Add one to start forwarding events.
                                </p>
                            ) : (
                                destinations.map((destination) => (
                                    <button
                                        key={destination.id}
                                        type="button"
                                        onClick={() => applyDestination(destination)}
                                        className={`w-full text-left rounded-xl border px-3 py-3 transition-colors ${
                                            selectedId === destination.id
                                                ? "border-blue-300 bg-blue-50/70 dark:border-blue-700 dark:bg-blue-950/40"
                                                : "border-border/50 hover:bg-muted/40"
                                        }`}
                                        data-testid={`button-siem-destination-${destination.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <p className="font-medium text-sm">{destination.destinationName}</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {destination.host}:{destination.port} · {destination.protocol}
                                                </p>
                                            </div>
                                            <Badge variant={destination.enabled ? "default" : "secondary"} className="shrink-0">
                                                {destination.enabled ? "On" : "Off"}
                                            </Badge>
                                        </div>
                                    </button>
                                ))
                            )}
                        </CardContent>
                    </Card>

                    <div className="space-y-6 max-w-4xl">
                        <Card className="border-border/40 rounded-2xl">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                    SIEM Connection
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-base font-medium">Enable destination</Label>
                                        <p className="text-sm text-muted-foreground">
                                            When enabled, matching events are forwarded to this SIEM.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={form.enabled}
                                        onCheckedChange={(checked) => updateField("enabled", checked)}
                                        data-testid="switch-siem-enabled"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2 md:col-span-2">
                                        <Label htmlFor="siem-destination-name">Destination Name</Label>
                                        <Input
                                            id="siem-destination-name"
                                            value={form.destinationName}
                                            onChange={(e) => updateField("destinationName", e.target.value)}
                                            placeholder="Primary Splunk / QRadar / Sentinel"
                                            data-testid="input-siem-destination-name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="siem-host">SIEM Host / IP</Label>
                                        <Input
                                            id="siem-host"
                                            value={form.host}
                                            onChange={(e) => updateField("host", e.target.value)}
                                            placeholder="10.10.10.25"
                                            data-testid="input-siem-host"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="siem-port">Port</Label>
                                        <Input
                                            id="siem-port"
                                            type="number"
                                            value={form.port}
                                            onChange={(e) => updateField("port", Number(e.target.value) || 1514)}
                                            placeholder="1514"
                                            data-testid="input-siem-port"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Protocol</Label>
                                        <Select
                                            value={form.protocol}
                                            onValueChange={(value: SiemProtocol) => {
                                                updateField("protocol", value);
                                                if (value !== "TLS") {
                                                    updateField("tlsMode", "NONE");
                                                } else if ((form.tlsMode || "NONE") === "NONE") {
                                                    updateField("tlsMode", "TLS");
                                                }
                                            }}
                                        >
                                            <SelectTrigger data-testid="select-siem-protocol">
                                                <SelectValue placeholder="Select protocol" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="TCP">Syslog TCP</SelectItem>
                                                <SelectItem value="UDP">Syslog UDP</SelectItem>
                                                <SelectItem value="TLS">Syslog TLS</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Message Format</Label>
                                        <Select
                                            value={form.messageFormat}
                                            onValueChange={(value: SiemMessageFormat) =>
                                                updateField("messageFormat", value)
                                            }
                                        >
                                            <SelectTrigger data-testid="select-siem-message-format">
                                                <SelectValue placeholder="Select format" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="CEF">CEF</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="siem-connection-timeout">Connection Timeout (sec)</Label>
                                        <Input
                                            id="siem-connection-timeout"
                                            type="number"
                                            value={form.connectionTimeoutSec ?? 10}
                                            onChange={(e) =>
                                                updateField("connectionTimeoutSec", Number(e.target.value) || 10)
                                            }
                                            data-testid="input-siem-connection-timeout"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="siem-read-timeout">Read Timeout (sec)</Label>
                                        <Input
                                            id="siem-read-timeout"
                                            type="number"
                                            value={form.readTimeoutSec ?? 30}
                                            onChange={(e) =>
                                                updateField("readTimeoutSec", Number(e.target.value) || 30)
                                            }
                                            data-testid="input-siem-read-timeout"
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        {isTlsTransport && (
                            <Card className="border-border/40 rounded-2xl">
                                <CardHeader>
                                    <CardTitle>SIEM Transport</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <div className="space-y-3">
                                        <Label className="text-base font-medium">TLS Authentication</Label>
                                        <div className="space-y-3">
                                            <label className="flex items-start gap-3 rounded-xl border border-border/50 p-4">
                                                <input
                                                    type="radio"
                                                    name="tls-mode"
                                                    checked={tlsMode === "TLS"}
                                                    onChange={() => updateField("tlsMode", "TLS")}
                                                    data-testid="radio-siem-tls-mode-tls"
                                                />
                                                <div className="space-y-1">
                                                    <p className="font-medium">TLS</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        Server verification only. Upload only the server CA when required.
                                                    </p>
                                                </div>
                                            </label>
                                            <label className="flex items-start gap-3 rounded-xl border border-border/50 p-4">
                                                <input
                                                    type="radio"
                                                    name="tls-mode"
                                                    checked={tlsMode === "MTLS"}
                                                    onChange={() => updateField("tlsMode", "MTLS")}
                                                    data-testid="radio-siem-tls-mode-mtls"
                                                />
                                                <div className="space-y-1">
                                                    <p className="font-medium">Mutual TLS</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        Client identity required. Use either a `.p12` / `.pfx` file or a client certificate and private key.
                                                    </p>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    {isServerTls && (
                                        <div className="space-y-4 rounded-xl border border-border/50 p-4">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <Label className="text-base font-medium">Server verification only</Label>
                                                    <p className="text-sm text-muted-foreground">
                                                        Do not upload client keys for this mode.
                                                    </p>
                                                </div>
                                                <Switch
                                                    checked={form.verifyServerCertificate ?? true}
                                                    onCheckedChange={(checked) => updateField("verifyServerCertificate", checked)}
                                                    data-testid="switch-siem-verify-cert"
                                                />
                                            </div>
                                            <TlsMaterialField
                                                label="CA Certificate"
                                                accept=".crt,.cer,.pem"
                                                testId="input-siem-ca-upload"
                                                stored={storedFlags.hasCaCertificate}
                                                storedFilename={storedFlags.caCertificateFilename}
                                                pendingFilename={form.caCertificateFilename}
                                                pendingLoaded={Boolean(form.caCertificate?.trim())}
                                                markedForRemoval={Boolean(form.clearCaCertificate)}
                                                onUpload={(file) =>
                                                    handleUploadField(
                                                        "caCertificate",
                                                        file,
                                                        "caCertificateFilename",
                                                        "clearCaCertificate",
                                                    )
                                                }
                                                onRemove={() =>
                                                    markTlsRemoval(
                                                        "clearCaCertificate",
                                                        "caCertificate",
                                                        "caCertificateFilename",
                                                    )
                                                }
                                            />
                                        </div>
                                    )}

                                    {isMutualTls && (
                                        <div className="space-y-6 rounded-xl border border-border/50 p-4">
                                            <div className="space-y-4">
                                                <Label className="text-base font-medium">Client Identity</Label>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <TlsMaterialField
                                                        label="Client identity (.p12 / .pfx)"
                                                        accept=".p12,.pfx"
                                                        testId="input-siem-keystore-upload"
                                                        stored={storedFlags.hasClientKeystore}
                                                        storedFilename={storedFlags.clientKeystoreFilename}
                                                        pendingFilename={form.clientKeystoreFilename}
                                                        pendingLoaded={Boolean(form.clientKeystore?.trim())}
                                                        markedForRemoval={Boolean(form.clearClientKeystore)}
                                                        onUpload={(file) =>
                                                            handleUploadField(
                                                                "clientKeystore",
                                                                file,
                                                                "clientKeystoreFilename",
                                                                "clearClientKeystore",
                                                            )
                                                        }
                                                        onRemove={() =>
                                                            markTlsRemoval(
                                                                "clearClientKeystore",
                                                                "clientKeystore",
                                                                "clientKeystoreFilename",
                                                                [
                                                                    "clearClientKeystorePassword",
                                                                    "clientKeystorePassword",
                                                                ],
                                                            )
                                                        }
                                                    />
                                                    <div className="space-y-2 rounded-xl border border-border/50 p-3">
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="space-y-1">
                                                                <Label htmlFor="siem-keystore-password">Keystore password</Label>
                                                                {(storedFlags.hasClientKeystorePassword ||
                                                                    Boolean(form.clientKeystorePassword?.trim())) &&
                                                                !form.clearClientKeystorePassword ? (
                                                                    <Badge variant="secondary" className="font-normal">
                                                                        {form.clientKeystorePassword?.trim()
                                                                            ? "Ready to save"
                                                                            : "Configured"}
                                                                    </Badge>
                                                                ) : form.clearClientKeystorePassword ? (
                                                                    <p className="text-sm text-amber-600 dark:text-amber-400">
                                                                        Will be removed on save
                                                                    </p>
                                                                ) : (
                                                                    <p className="text-sm text-muted-foreground">Not configured</p>
                                                                )}
                                                            </div>
                                                            {(storedFlags.hasClientKeystorePassword ||
                                                                Boolean(form.clientKeystorePassword?.trim())) &&
                                                                !form.clearClientKeystorePassword && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="text-destructive hover:text-destructive"
                                                                        onClick={() =>
                                                                            markTlsRemoval(
                                                                                "clearClientKeystorePassword",
                                                                                "clientKeystorePassword",
                                                                            )
                                                                        }
                                                                        data-testid="input-siem-keystore-password-remove"
                                                                    >
                                                                        <Trash2 className="h-4 w-4 mr-1" />
                                                                        Remove
                                                                    </Button>
                                                                )}
                                                        </div>
                                                        <Input
                                                            id="siem-keystore-password"
                                                            type="password"
                                                            value={form.clientKeystorePassword}
                                                            onChange={(e) => {
                                                                updateField("clientKeystorePassword", e.target.value);
                                                                updateField("clearClientKeystorePassword", false);
                                                            }}
                                                            placeholder={
                                                                storedFlags.hasClientKeystorePassword &&
                                                                !form.clearClientKeystorePassword
                                                                    ? "•••••••• (leave blank to keep)"
                                                                    : "Keystore password"
                                                            }
                                                            data-testid="input-siem-keystore-password"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="text-sm text-muted-foreground">OR</div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <TlsMaterialField
                                                    label="Client Certificate"
                                                    accept=".crt,.cer,.pem"
                                                    testId="input-siem-client-cert-upload"
                                                    stored={storedFlags.hasClientCertificate}
                                                    storedFilename={storedFlags.clientCertificateFilename}
                                                    pendingFilename={form.clientCertificateFilename}
                                                    pendingLoaded={Boolean(form.clientCertificate?.trim())}
                                                    markedForRemoval={Boolean(form.clearClientCertificate)}
                                                    onUpload={(file) =>
                                                        handleUploadField(
                                                            "clientCertificate",
                                                            file,
                                                            "clientCertificateFilename",
                                                            "clearClientCertificate",
                                                        )
                                                    }
                                                    onRemove={() =>
                                                        markTlsRemoval(
                                                            "clearClientCertificate",
                                                            "clientCertificate",
                                                            "clientCertificateFilename",
                                                        )
                                                    }
                                                />
                                                <TlsMaterialField
                                                    label="Private Key"
                                                    accept=".key,.pem"
                                                    testId="input-siem-client-key-upload"
                                                    stored={storedFlags.hasClientPrivateKey}
                                                    storedFilename={storedFlags.clientPrivateKeyFilename}
                                                    pendingFilename={form.clientPrivateKeyFilename}
                                                    pendingLoaded={Boolean(form.clientPrivateKey?.trim())}
                                                    markedForRemoval={Boolean(form.clearClientPrivateKey)}
                                                    onUpload={(file) =>
                                                        handleUploadField(
                                                            "clientPrivateKey",
                                                            file,
                                                            "clientPrivateKeyFilename",
                                                            "clearClientPrivateKey",
                                                        )
                                                    }
                                                    onRemove={() =>
                                                        markTlsRemoval(
                                                            "clearClientPrivateKey",
                                                            "clientPrivateKey",
                                                            "clientPrivateKeyFilename",
                                                            ["clearPrivateKeyPassword", "privateKeyPassword"],
                                                        )
                                                    }
                                                />
                                                <TlsMaterialField
                                                    label="CA Certificate"
                                                    accept=".crt,.cer,.pem"
                                                    testId="input-siem-ca-upload-mtls"
                                                    stored={storedFlags.hasCaCertificate}
                                                    storedFilename={storedFlags.caCertificateFilename}
                                                    pendingFilename={form.caCertificateFilename}
                                                    pendingLoaded={Boolean(form.caCertificate?.trim())}
                                                    markedForRemoval={Boolean(form.clearCaCertificate)}
                                                    onUpload={(file) =>
                                                        handleUploadField(
                                                            "caCertificate",
                                                            file,
                                                            "caCertificateFilename",
                                                            "clearCaCertificate",
                                                        )
                                                    }
                                                    onRemove={() =>
                                                        markTlsRemoval(
                                                            "clearCaCertificate",
                                                            "caCertificate",
                                                            "caCertificateFilename",
                                                        )
                                                    }
                                                />
                                                <div className="space-y-2 rounded-xl border border-border/50 p-3">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="space-y-1">
                                                            <Label htmlFor="siem-key-password">Private key password</Label>
                                                            {(storedFlags.hasPrivateKeyPassword ||
                                                                Boolean(form.privateKeyPassword?.trim())) &&
                                                            !form.clearPrivateKeyPassword ? (
                                                                <Badge variant="secondary" className="font-normal">
                                                                    {form.privateKeyPassword?.trim()
                                                                        ? "Ready to save"
                                                                        : "Configured"}
                                                                </Badge>
                                                            ) : form.clearPrivateKeyPassword ? (
                                                                <p className="text-sm text-amber-600 dark:text-amber-400">
                                                                    Will be removed on save
                                                                </p>
                                                            ) : (
                                                                <p className="text-sm text-muted-foreground">
                                                                    Optional — only if the key is encrypted
                                                                </p>
                                                            )}
                                                        </div>
                                                        {(storedFlags.hasPrivateKeyPassword ||
                                                            Boolean(form.privateKeyPassword?.trim())) &&
                                                            !form.clearPrivateKeyPassword && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-destructive hover:text-destructive"
                                                                    onClick={() =>
                                                                        markTlsRemoval(
                                                                            "clearPrivateKeyPassword",
                                                                            "privateKeyPassword",
                                                                        )
                                                                    }
                                                                    data-testid="input-siem-key-password-remove"
                                                                >
                                                                    <Trash2 className="h-4 w-4 mr-1" />
                                                                    Remove
                                                                </Button>
                                                            )}
                                                    </div>
                                                    <Input
                                                        id="siem-key-password"
                                                        type="password"
                                                        value={form.privateKeyPassword}
                                                        onChange={(e) => {
                                                            updateField("privateKeyPassword", e.target.value);
                                                            updateField("clearPrivateKeyPassword", false);
                                                        }}
                                                        placeholder={
                                                            storedFlags.hasPrivateKeyPassword &&
                                                            !form.clearPrivateKeyPassword
                                                                ? "•••••••• (leave blank to keep)"
                                                                : "If private key is encrypted"
                                                        }
                                                        data-testid="input-siem-key-password"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                            <Card className="border-border/40 rounded-2xl" data-testid="card-siem-filters">
                                <CollapsibleTrigger asChild>
                                    <CardHeader className="cursor-pointer select-none hover:bg-muted/30 rounded-t-2xl transition-colors">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="space-y-1.5 min-w-0">
                                                <CardTitle>Filters</CardTitle>
                                                <CardDescription>
                                                    Limit which severities and event categories are forwarded to this
                                                    SIEM. Leave everything unchecked to forward all events.
                                                </CardDescription>
                                            </div>
                                            <ChevronDown
                                                className={cn(
                                                    "h-5 w-5 shrink-0 text-muted-foreground transition-transform mt-0.5",
                                                    filtersOpen && "rotate-180",
                                                )}
                                            />
                                        </div>
                                    </CardHeader>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Severity Filter</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Leave all unchecked to forward every severity.
                                            </p>
                                            <div className="flex flex-wrap gap-3">
                                                {severities.map((severity) => {
                                                    const checked = parseCsv(form.severityFilter).includes(severity);
                                                    return (
                                                        <label
                                                            key={severity}
                                                            className="inline-flex items-center gap-2 rounded-lg border border-border/50 px-3 py-2 text-sm"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) =>
                                                                    updateField(
                                                                        "severityFilter",
                                                                        toggleCsvValue(
                                                                            form.severityFilter,
                                                                            severity,
                                                                            e.target.checked,
                                                                        ),
                                                                    )
                                                                }
                                                                data-testid={`checkbox-siem-severity-${severity.toLowerCase()}`}
                                                            />
                                                            {severity}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Event Category Filter</Label>
                                            <p className="text-sm text-muted-foreground">
                                                Options come from available log types (Audit, Alerts, AI). Leave
                                                unchecked to forward all.
                                            </p>
                                            <div className="flex flex-wrap gap-3">
                                                {eventCategories.map((category) => {
                                                    const checked =
                                                        parseCsv(form.eventCategoryFilter).includes(category);
                                                    return (
                                                        <label
                                                            key={category}
                                                            className="inline-flex items-center gap-2 rounded-lg border border-border/50 px-3 py-2 text-sm"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={(e) =>
                                                                    updateField(
                                                                        "eventCategoryFilter",
                                                                        toggleCsvValue(
                                                                            form.eventCategoryFilter,
                                                                            category,
                                                                            e.target.checked,
                                                                        ),
                                                                    )
                                                                }
                                                                data-testid={`checkbox-siem-category-${category.toLowerCase()}`}
                                                            />
                                                            {category}
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </CardContent>
                                </CollapsibleContent>
                            </Card>
                        </Collapsible>

                        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                            <Card className="border-border/40 rounded-2xl" data-testid="card-siem-advanced">
                                <CollapsibleTrigger asChild>
                                    <CardHeader className="cursor-pointer select-none hover:bg-muted/30 rounded-t-2xl transition-colors">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="space-y-1.5 min-w-0">
                                                <CardTitle>Advanced</CardTitle>
                                                <CardDescription>
                                                    Optional transport tuning: TLS version, keep-alive, retries, and
                                                    outbound queue size. Defaults work for most SIEM destinations.
                                                </CardDescription>
                                            </div>
                                            <ChevronDown
                                                className={cn(
                                                    "h-5 w-5 shrink-0 text-muted-foreground transition-transform mt-0.5",
                                                    advancedOpen && "rotate-180",
                                                )}
                                            />
                                        </div>
                                    </CardHeader>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <CardContent className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>TLS Version</Label>
                                                <Select
                                                    value={form.tlsVersion || "AUTO"}
                                                    onValueChange={(value: SiemTlsVersion) =>
                                                        updateField("tlsVersion", value)
                                                    }
                                                    disabled={!isTlsTransport}
                                                >
                                                    <SelectTrigger data-testid="select-siem-tls-version">
                                                        <SelectValue placeholder="Select TLS version" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="AUTO">Auto</SelectItem>
                                                        <SelectItem value="TLS_1_2">TLS 1.2</SelectItem>
                                                        <SelectItem value="TLS_1_3">TLS 1.3</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Keep Alive</Label>
                                                <Select
                                                    value={form.keepAlive ? "YES" : "NO"}
                                                    onValueChange={(value) =>
                                                        updateField("keepAlive", value === "YES")
                                                    }
                                                >
                                                    <SelectTrigger data-testid="select-siem-keep-alive">
                                                        <SelectValue placeholder="Keep alive" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="YES">Yes</SelectItem>
                                                        <SelectItem value="NO">No</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="siem-retry-attempts">Retry Attempts</Label>
                                                <Input
                                                    id="siem-retry-attempts"
                                                    type="number"
                                                    value={form.retryAttempts ?? 3}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "retryAttempts",
                                                            Number(e.target.value) || 0,
                                                        )
                                                    }
                                                    data-testid="input-siem-retry-attempts"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="siem-retry-interval">Retry Interval (sec)</Label>
                                                <Input
                                                    id="siem-retry-interval"
                                                    type="number"
                                                    value={form.retryIntervalSec ?? 30}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "retryIntervalSec",
                                                            Number(e.target.value) || 30,
                                                        )
                                                    }
                                                    data-testid="input-siem-retry-interval"
                                                />
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <Label htmlFor="siem-queue-capacity">Queue Capacity</Label>
                                                <Input
                                                    id="siem-queue-capacity"
                                                    type="number"
                                                    value={form.queueCapacity ?? 1000}
                                                    onChange={(e) =>
                                                        updateField(
                                                            "queueCapacity",
                                                            Number(e.target.value) || 1000,
                                                        )
                                                    }
                                                    data-testid="input-siem-queue-capacity"
                                                />
                                            </div>
                                        </div>
                                    </CardContent>
                                </CollapsibleContent>
                            </Card>
                        </Collapsible>

                        <div className="flex flex-wrap gap-3">
                            <Button onClick={handleSave} disabled={saving} data-testid="button-save-siem">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Save Destination
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleTestConnection}
                                disabled={testingConnection || selectedId == null}
                                className="gap-2"
                                data-testid="button-test-siem-connection"
                            >
                                {testingConnection ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <PlugZap className="w-4 h-4" />
                                )}
                                Test Connection
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleSendTestEvent}
                                disabled={sendingTestEvent || selectedId == null}
                                className="gap-2"
                                data-testid="button-send-siem-test-event"
                            >
                                {sendingTestEvent ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Send className="w-4 h-4" />
                                )}
                                Send Test Event
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => setDeleteDialogOpen(true)}
                                disabled={selectedId == null}
                                className="gap-2"
                                data-testid="button-delete-siem"
                            >
                                <Trash2 className="w-4 h-4" />
                                Remove Destination
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove SIEM Destination</AlertDialogTitle>
                        <AlertDialogDescription>
                            This deletes the selected destination and stops forwarding events to it.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
